import type { LeaveTypeId } from '@lms/shared-types'
import { fmtDateLong } from '../../lib/format'

/**
 * Shared rich-tooltip body for any "leave bar" surface — team calendar,
 * out-this-week strip, leave-detail team coverage. Keeps every Gantt-style
 * leave pill consistent: bold employee name, friendly leave-type label with
 * day count, locale-formatted date range, optional team name and emergency
 * marker.
 *
 * Date inputs are ISO date strings ("YYYY-MM-DD") and are read in UTC so the
 * weekday matches the display in the bar regardless of viewer timezone.
 */
export interface LeaveBarTooltipProps {
  employeeName: string
  /** Either the typed LeaveTypeId or a free-text label from the API. */
  leaveType: LeaveTypeId | string
  startDate?: string
  endDate?: string
  halfDay?: boolean
  /** Optional secondary line — e.g. "Engineering" or the requester's team. */
  teamName?: string
  /** Renders an "Emergency" tag when true. */
  isEmergency?: boolean
  /** Adds a "Requester" tag — used on the leave-detail coverage strip. */
  isRequester?: boolean
  /** Optional status — renders a "Pending" tag when not 'approved'. */
  status?: 'approved' | 'pending'
  /**
   * When set, surfaces a "Conflicts with <reason>" line above any free-text
   * reason. Rendered in clay tone to signal the conflict.
   */
  conflictReason?: string
  /**
   * Free-text leave reason. Rendered as a 2-line clamped snippet ending in
   * "…" when overflowed. Skipped when nullish or whitespace-only.
   */
  reason?: string | null
  /**
   * Suppresses the start–end date-range line. Used when a day-by-day breakdown
   * is rendered directly below the tooltip, which already lists every date, so
   * the range would be redundant.
   */
  hideDateRange?: boolean
  /**
   * Working-day count to show instead of the inclusive calendar-day span.
   * Passed alongside a day-by-day breakdown so the header matches the list
   * (weekends and public holidays excluded, half days as 0.5).
   */
  dayCount?: number
}

export function LeaveBarTooltip({
  employeeName,
  leaveType,
  startDate,
  endDate,
  halfDay = false,
  teamName,
  isEmergency = false,
  isRequester = false,
  status,
  conflictReason,
  reason,
  hideDateRange = false,
  dayCount,
}: LeaveBarTooltipProps): JSX.Element {
  const isPending = status === 'pending'
  const reasonSnippet = reason?.trim()
  const hasReason = reasonSnippet !== undefined && reasonSnippet.length > 0
  const typeLabel = leaveTypeLabel(leaveType)
  const durationLabel = formatDurationLabel({ startDate, endDate, halfDay, dayCount })
  const dateRange =
    startDate !== undefined && endDate !== undefined
      ? formatDateRange(startDate, endDate)
      : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 12 }}>{employeeName}{teamName !== undefined ? (<span style={{ fontSize: 11, opacity: 0.7 }}> · {teamName}</span>) : null}</div>
      <div style={{ fontSize: 11, opacity: 0.85 }}>
        {typeLabel} leave{durationLabel !== null ? ` · ${durationLabel}` : ''}
      </div>
      {dateRange !== null && !hideDateRange ? (
        <div style={{ fontSize: 11, opacity: 0.7 }}>{dateRange}</div>
      ) : null}
      {conflictReason !== undefined ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--danger-fg)',
            fontWeight: 600,
            marginTop: 4,
          }}
          data-testid="leavebar-tooltip-conflict"
        >
          Conflicts with {conflictReason}
        </div>
      ) : null}
      {hasReason ? (
        <div
          style={{
            fontSize: 11,
            fontStyle: 'italic',
            opacity: 0.85,
            marginTop: 4,
            paddingTop: 4,
            borderTop: '1px solid rgba(255, 255, 255, 0.15)',
            // 2-line clamp with trailing "…" when overflowed. Width is bounded
            // by Tooltip's maxWidth (240px), so this naturally trims long
            // reasons without slicing mid-word in JS.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          “{reasonSnippet}”
        </div>
      ) : null}
      {isEmergency || isRequester || isPending ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {isRequester ? <Tag tone="terracotta">Requester</Tag> : null}
          {isEmergency ? <Tag tone="clay">Emergency</Tag> : null}
          {isPending ? <Tag tone="ochre">Pending</Tag> : null}
        </div>
      ) : null}
    </div>
  )
}

interface TagProps {
  tone: 'terracotta' | 'clay' | 'ochre'
  children: string
}

function Tag({ tone, children }: TagProps): JSX.Element {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        padding: '1px 6px',
        borderRadius: 4,
        background: `var(--${tone}-bg)`,
        color: `var(--${tone}-fg)`,
        border: `1px solid var(--${tone}-line)`,
      }}
    >
      {children}
    </span>
  )
}

function formatDurationLabel(args: {
  startDate: string | undefined
  endDate: string | undefined
  halfDay: boolean
  dayCount: number | undefined
}): string | null {
  if (args.halfDay) return '½ day'
  if (args.dayCount !== undefined) {
    if (args.dayCount === 0.5) return '½ day'
    return `${args.dayCount} ${args.dayCount === 1 ? 'day' : 'days'}`
  }
  if (args.startDate === undefined || args.endDate === undefined) return null
  const days = countInclusiveDays(args.startDate, args.endDate)
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

function countInclusiveDays(startIso: string, endIso: string): number {
  const start = isoToUtcMs(startIso)
  const end = isoToUtcMs(endIso)
  return Math.max(1, Math.round((end - start) / 86_400_000) + 1)
}

function isoToUtcMs(iso: string): number {
  return Date.UTC(
    Number(iso.slice(0, 4)),
    Number(iso.slice(5, 7)) - 1,
    Number(iso.slice(8, 10)),
  )
}

function formatDateRange(startIso: string, endIso: string): string {
  if (startIso === endIso) return fmtDateLong(startIso)
  // Full "dd MMM yyyy" on both endpoints — "08 Jun 2026 – 11 Jun 2026".
  return `${fmtDateLong(startIso)} – ${fmtDateLong(endIso)}`
}

/** Map a typed LeaveTypeId or free-text label to a friendly capitalised name. */
export function leaveTypeLabel(type: LeaveTypeId | string): string {
  switch (type) {
    case 'hidden':
      // Privacy sentinel — composed as "On leave" (type withheld).
      return 'On'
    case 'annual':
      return 'Annual'
    case 'medical':
      return 'Medical'
    case 'birthday':
      return 'Birthday'
    case 'replacement':
      return 'Replacement'
    case 'compassionate':
      return 'Compassionate'
    case 'marriage':
      return 'Marriage'
    case 'marriage_family_care':
      return 'Marriage & Family Care'
    case 'unpaid':
      return 'Unpaid'
    case 'maternity':
      return 'Maternity'
    case 'paternity':
      return 'Paternity'
    case 'hospitalization':
      return 'Hospitalization'
    default:
      // Free-text from API — capitalise first letter, leave rest untouched.
      if (type.length === 0) return 'Leave'
      return type[0]!.toUpperCase() + type.slice(1)
  }
}
